<template>
    <div class="flex flex-col gap-4 text-ink">
        <!-- Step 1: Upload -->
        <template v-if="step === 'upload'">
            <I18nT keypath="connections.csvImport.upload.intro" tag="p" class="body-small text-ink-muted">
                <template #column>
                    <strong>{{ NAME_COLUMN }}</strong>
                </template>
            </I18nT>

            <BaseFileUpload
                v-if="!selectedFile"
                v-slot="{ clickInput }"
                override-document-upload-flag
                accepted-file-types=".csv"
                :is-disabled="isUploading"
                @file-upload="handleFileSelect">
                <BaseButton theme="neutral" class="w-full md:hidden" :disabled="isUploading" @click="clickInput">
                    <i class="bi bi-file-earmark-arrow-up text-lg" aria-hidden="true"></i>
                    {{ t("connections.csvImport.upload.mobileButton") }}
                </BaseButton>
                <BaseCard
                    as="button"
                    type="button"
                    padding="none"
                    radius="lgr"
                    :disabled="isUploading"
                    class="hidden md:block w-full border-2 border-dashed border-control-border p-10 text-center text-ink-muted enabled:hover:border-control-border-strong enabled:hover:bg-surface-hover"
                    @click="clickInput">
                    <i class="bi bi-cloud-arrow-up text-3xl mb-2 block" aria-hidden="true"></i>
                    <p class="body-small">{{ t("connections.csvImport.upload.dropzone") }}</p>
                    <p class="details mt-1">{{ t("connections.csvImport.upload.dropzoneHint") }}</p>
                </BaseCard>
            </BaseFileUpload>

            <!-- File info card (shown after file selected) -->
            <div v-if="selectedFile" class="flex flex-col">
                <div class="border border-border rounded-xl p-4 flex flex-col gap-3">
                    <div class="flex items-center gap-3">
                        <div class="rounded-full border-2 border-border min-w-10 min-h-10 flex items-center justify-center details uppercase text-ink-muted">
                            {{ t("connections.csvImport.upload.fileTypeBadge") }}
                        </div>
                        <div class="grow min-w-0">
                            <div class="body-small-semibold truncate">{{ truncatedFileName }}</div>
                            <span class="details text-ink-muted">{{ formattedFileSize }}</span>
                        </div>
                        <BaseButton theme="ghost" size="sm" icon-only :aria-label="t('connections.csvImport.upload.removeFileLabel')" @click="clearFile">
                            <i class="bi bi-x leading-none" aria-hidden="true"></i>
                        </BaseButton>
                    </div>
                    <!-- Parsed file info -->
                    <div v-if="parsedFileInfo" class="flex flex-wrap items-center gap-2 details">
                        <BaseChip size="sm" class="text-ink-muted">{{
                            t("connections.csvImport.upload.rowCount", { count: parsedFileInfo.rowCount }, parsedFileInfo.rowCount)
                        }}</BaseChip>
                        <span v-for="col in parsedFileInfo.detectedColumns" :key="col" class="min-w-0 max-w-full wrap-anywhere text-ink-muted">
                            <i v-if="expectedColumns.includes(col)" class="bi bi-check" aria-hidden="true"></i>
                            {{ col }}
                        </span>
                        <span v-for="col in missingExpectedColumns" :key="'missing-' + col" class="text-button-danger">
                            {{ t("connections.csvImport.upload.missingColumn", { column: col }) }}
                        </span>
                    </div>
                </div>
                <BaseButton theme="tertiary" size="sm" class="md:hidden mt-1" @click="clearFile">{{ t("connections.csvImport.upload.replaceFile") }}</BaseButton>
            </div>

            <BaseNotification v-if="uploadError" theme="error" hide-dismiss :text="uploadError" />

            <BaseButton theme="neutral" size="sm" class="inline-flex items-center gap-2 w-fit" @click="downloadTemplate">
                <i class="bi bi-download" aria-hidden="true"></i>
                {{ t("connections.csvImport.upload.downloadTemplate") }}
            </BaseButton>

            <!-- Column reference (collapsible) -->
            <div>
                <BaseButton theme="tertiary" size="sm" class="flex items-center gap-2" :aria-expanded="columnRefOpen" @click="columnRefOpen = !columnRefOpen">
                    <i :class="columnRefOpen ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi text-[10px]" aria-hidden="true"></i>
                    {{ t("connections.csvImport.upload.viewColumnRequirements") }}
                </BaseButton>
                <div v-show="columnRefOpen" class="mt-2">
                    <!-- Mobile: stacked card list -->
                    <div class="md:hidden space-y-2">
                        <div v-for="col in columnReference" :key="col.column" class="border border-border rounded-lg px-3 py-2.5">
                            <div v-if="col.required" class="details text-ink">
                                {{ col.column }} <span class="font-normal text-ink-muted">{{ t("connections.csvImport.columns.requiredMarker") }}</span>
                            </div>
                            <div v-else class="details text-ink-muted">{{ col.column }}</div>
                            <div class="details text-ink-muted mt-1">{{ col.format }}</div>
                            <div class="details text-ink-muted mt-1">{{ t("connections.csvImport.columns.exampleLabel", { example: col.example }) }}</div>
                        </div>
                    </div>
                    <!-- Desktop: table -->
                    <div class="hidden md:block border border-border rounded-lg overflow-hidden">
                        <table class="w-full details">
                            <thead class="bg-surface-sunken text-ink-muted">
                                <tr>
                                    <th class="text-left px-3 py-2 font-medium">{{ t("connections.csvImport.columns.headerColumn") }}</th>
                                    <th class="text-left px-3 py-2 font-medium">{{ t("connections.csvImport.columns.headerRequired") }}</th>
                                    <th class="text-left px-3 py-2 font-medium">{{ t("connections.csvImport.columns.headerFormat") }}</th>
                                    <th class="text-left px-3 py-2 font-medium">{{ t("connections.csvImport.columns.headerExample") }}</th>
                                </tr>
                            </thead>
                            <tbody class="text-ink-muted">
                                <tr v-for="col in columnReference" :key="col.column" class="border-t border-border">
                                    <td class="px-3 py-2" :class="col.required ? 'font-semibold text-ink' : 'text-ink-muted'">{{ col.column }}</td>
                                    <td class="px-3 py-2">{{ col.required ? t("connections.csvImport.columns.requiredYes") : "" }}</td>
                                    <td class="px-3 py-2">{{ col.format }}</td>
                                    <td class="px-3 py-2">{{ col.example }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="details text-ink-muted mt-2">{{ t("connections.csvImport.upload.tip") }}</p>
                </div>
            </div>
        </template>

        <!-- Step 2: Preview -->
        <template v-else-if="step === 'preview'">
            <div>
                <h3 class="body-bold text-ink">{{ t("connections.csvImport.preview.title") }}</h3>
                <p class="body-small text-ink-muted mt-1">{{ t("connections.csvImport.preview.description") }}</p>
            </div>
            <div class="flex flex-wrap items-center gap-3 body-small text-ink-muted">
                <BaseChip class="text-ink-muted">{{ t("connections.csvImport.preview.newCount", { count: previewData.summary.new }, previewData.summary.new) }}</BaseChip>
                <StatusChip state="pending">{{
                    t("connections.csvImport.preview.duplicateCount", { count: previewData.summary.duplicates }, previewData.summary.duplicates)
                }}</StatusChip>
                <span v-if="previewData.summary.errors > 0" class="text-button-danger">{{
                    t("connections.csvImport.preview.errorCount", { count: previewData.summary.errors }, previewData.summary.errors)
                }}</span>
            </div>
            <!-- Auto-overwrite toggle -->
            <div v-if="previewData.summary.duplicates > 0" class="flex flex-col gap-1">
                <BaseCheckbox v-model="autoOverwrite" checkbox-id="csv-auto-overwrite" shape="square">
                    <span class="body-small text-control-label cursor-pointer">{{ t("connections.csvImport.preview.autoOverwriteLabel") }}</span>
                </BaseCheckbox>
                <p class="details text-ink-muted ml-10">{{ t("connections.csvImport.preview.autoOverwriteDescription") }}</p>
            </div>

            <!-- Mobile: stacked card list -->
            <div class="md:hidden max-h-96 overflow-y-auto space-y-2">
                <div
                    v-for="row in previewData.rows"
                    :key="'m-' + row.row_index"
                    class="border rounded-lg p-3"
                    :class="{
                        'border-status-error bg-status-error-soft': row.errors.length > 0,
                        'border-notification-warning-border bg-chip-status-pending-bg': row.is_duplicate && !row.errors.length && !overwriteFlash,
                        'border-notification-warning-border overwrite-flash': row.is_duplicate && !row.errors.length && overwriteFlash,
                        'border-border': !row.is_duplicate && !row.errors.length,
                    }">
                    <div class="flex items-center justify-between gap-2">
                        <span class="body-small-semibold truncate">{{ row.name || t("connections.csvImport.preview.emptyName") }}</span>
                        <span v-if="row.errors.length > 0" class="shrink-0 details text-button-danger-hover">
                            {{ row.errors.length === 1 ? row.errors[0] : t("connections.csvImport.preview.issueCount", { count: row.errors.length }, row.errors.length) }}
                        </span>
                        <StatusChip v-else-if="row.is_duplicate" state="pending">{{ t("connections.csvImport.preview.duplicate") }}</StatusChip>
                        <BaseChip v-else class="text-ink-muted">{{ t("connections.csvImport.preview.new") }}</BaseChip>
                    </div>
                    <div v-if="row.email || row.job_title" class="mt-1 details truncate" :class="row.errors.length ? 'text-ink' : 'text-ink-muted'">
                        <span v-if="row.email">{{ row.email }}</span>
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation separator -->
                        <span v-if="row.email && row.job_title"> · </span>
                        <span v-if="row.job_title">{{ row.job_title }}</span>
                    </div>
                    <!-- Segmented control for duplicates -->
                    <div v-if="row.is_duplicate && !row.errors.length && !autoOverwrite" class="flex justify-end mt-2">
                        <BaseToggleGroup
                            v-model="rowActions[row.row_index]"
                            :options="duplicateActionOptions"
                            :aria-label="t('connections.csvImport.preview.rowActionLabel', { name: row.name })" />
                    </div>
                </div>
            </div>

            <!-- Desktop: table -->
            <div class="hidden md:block max-h-80 overflow-y-auto border border-border-strong rounded-lg">
                <table class="w-full body-small">
                    <thead class="bg-surface-sunken sticky top-0">
                        <tr>
                            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: row number column header -->
                            <th class="text-left p-2 font-medium text-ink-muted w-8">#</th>
                            <th class="text-left p-2 font-medium">{{ t("connections.csvImport.preview.headerName") }}</th>
                            <th class="text-left p-2 font-medium">{{ t("connections.csvImport.preview.headerEmail") }}</th>
                            <th class="text-left p-2 font-medium">{{ t("connections.csvImport.preview.headerJobTitle") }}</th>
                            <th class="text-left p-2 font-medium">{{ t("connections.csvImport.preview.headerStatus") }}</th>
                            <th v-if="!autoOverwrite" class="text-left p-2 font-medium">{{ t("connections.csvImport.preview.headerAction") }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in previewData.rows"
                            :key="row.row_index"
                            :class="{
                                'bg-status-error-soft': row.errors.length > 0,
                                'bg-chip-status-pending-bg': row.is_duplicate && !row.errors.length && !overwriteFlash,
                                'overwrite-flash': row.is_duplicate && !row.errors.length && overwriteFlash,
                            }">
                            <td class="p-2" :class="row.errors.length ? 'text-ink' : 'text-ink-muted'">{{ row.row_index + 1 }}</td>
                            <td class="p-2">{{ row.name || t("connections.csvImport.preview.emptyName") }}</td>
                            <td class="p-2" :class="row.errors.length ? 'text-ink' : 'text-ink-muted'">{{ row.email || "-" }}</td>
                            <td class="p-2" :class="row.errors.length ? 'text-ink' : 'text-ink-muted'">{{ row.job_title || "-" }}</td>
                            <td class="p-2">
                                <template v-if="row.errors.length > 1">
                                    <BaseTooltip side="top">
                                        <template #trigger>
                                            <span class="text-button-danger-hover cursor-help underline decoration-dotted">{{
                                                t("connections.csvImport.preview.issueCount", { count: row.errors.length }, row.errors.length)
                                            }}</span>
                                        </template>
                                        <template #content>
                                            <ul class="details list-disc pl-3 py-1 space-y-1">
                                                <li v-for="(err, idx) in row.errors" :key="idx">{{ err }}</li>
                                            </ul>
                                        </template>
                                    </BaseTooltip>
                                </template>
                                <template v-else-if="row.errors.length === 1">
                                    <BaseTooltip side="top">
                                        <template #trigger>
                                            <span class="text-button-danger-hover cursor-help underline decoration-dotted">{{ row.errors[0] }}</span>
                                        </template>
                                        <template #content>
                                            <span class="details">{{ row.errors[0] }}</span>
                                        </template>
                                    </BaseTooltip>
                                </template>
                                <span v-else-if="row.is_duplicate && autoOverwrite" class="text-chip-status-pending-text">{{
                                    t("connections.csvImport.preview.willBeUpdated")
                                }}</span>
                                <span v-else-if="row.is_duplicate" class="text-chip-status-pending-text">{{
                                    t("connections.csvImport.preview.duplicateOf", { name: row.existing_connection_name })
                                }}</span>
                                <span v-else class="text-ink">{{ t("connections.csvImport.preview.new") }}</span>
                            </td>
                            <td v-if="!autoOverwrite" class="p-2">
                                <BaseToggleGroup
                                    v-if="row.is_duplicate && !row.errors.length"
                                    v-model="rowActions[row.row_index]"
                                    :options="duplicateActionOptions"
                                    :aria-label="t('connections.csvImport.preview.rowActionLabel', { name: row.name })" />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <!-- Step 3: Importing (progress) -->
        <template v-else-if="step === 'importing'">
            <div class="text-center py-10">
                <div class="overflow-hidden mb-3"><i class="bi bi-arrow-repeat text-3xl text-ink-muted block animate-spin" aria-hidden="true"></i></div>
                <h3 class="title-body mb-2">{{ t("connections.csvImport.importing.title") }}</h3>
                <template v-if="importProgress">
                    <p class="body-small text-ink-muted mb-4">{{ progressText }}</p>
                    <BaseProgressBar :value="progressPercent" :aria-label="progressText" />
                </template>
            </div>
        </template>

        <!-- Step 4: Result -->
        <template v-else-if="step === 'result'">
            <div class="text-center py-4">
                <i class="bi bi-check-circle text-4xl text-notification-success-text mb-3 block" aria-hidden="true"></i>
                <h3 class="title-body mb-2">{{ t("connections.csvImport.result.title") }}</h3>
                <p class="body-small text-ink-muted mb-3">{{ resultSummaryText }}</p>
                <div class="flex flex-wrap justify-center items-center gap-3 body-small text-ink-muted mb-4">
                    <StatusChip v-if="importResult.created" state="done">{{
                        t("connections.csvImport.result.createdCount", { count: importResult.created }, importResult.created)
                    }}</StatusChip>
                    <StatusChip v-if="importResult.updated" state="done">{{
                        t("connections.csvImport.result.updatedCount", { count: importResult.updated }, importResult.updated)
                    }}</StatusChip>
                    <BaseChip v-if="importResult.skipped" class="text-ink-muted">{{
                        t("connections.csvImport.result.skippedCount", { count: importResult.skipped }, importResult.skipped)
                    }}</BaseChip>
                    <span v-if="importResult.errors.length" class="text-button-danger">{{
                        t("connections.csvImport.result.errorCount", { count: importResult.errors.length }, importResult.errors.length)
                    }}</span>
                </div>
                <BaseCollapsible v-if="importResult.errors.length" v-model:open="errorDetailsOpen" class="text-left mb-4 mx-auto max-w-md">
                    <template #trigger>
                        <span class="flex items-center gap-2 body-small text-button-danger cursor-pointer hover:text-button-danger-hover">
                            <i :class="errorDetailsOpen ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi text-[10px]" aria-hidden="true"></i>
                            {{ t("connections.csvImport.result.showErrorDetails") }}
                        </span>
                    </template>
                    <template #content>
                        <ul class="mt-2 space-y-1 body-small text-ink-muted">
                            <li v-for="(error, idx) in importResult.errors" :key="idx" class="px-3 py-2 bg-status-error-soft rounded text-button-danger-hover">
                                {{ t("connections.csvImport.result.errorRow", { row: error.row, message: error.message }) }}
                            </li>
                        </ul>
                    </template>
                </BaseCollapsible>
            </div>
        </template>
    </div>
    <Teleport v-if="step !== 'importing'" defer :to="footerTarget">
        <template v-if="step === 'upload'">
            <BaseButton theme="tertiary" @click="emit('close')">{{ t("common.cancel") }}</BaseButton>
            <BaseButton theme="primary" :disabled="!selectedFile || isUploading" @click="handleUpload">{{
                isUploading ? t("connections.csvImport.footer.uploading") : t("connections.csvImport.footer.previewImport")
            }}</BaseButton>
        </template>
        <template v-else-if="step === 'preview'">
            <BaseButton theme="tertiary" @click="resetToUpload">{{ t("connections.csvImport.footer.back") }}</BaseButton>
            <BaseButton theme="primary" :disabled="isImporting || !hasImportableRows" @click="handleImport">{{
                isImporting ? t("connections.csvImport.footer.importing") : t("connections.csvImport.footer.confirmImport")
            }}</BaseButton>
        </template>
        <BaseButton v-else-if="step === 'result'" theme="primary" @click="handleDone">{{ t("connections.csvImport.footer.done") }}</BaseButton>
    </Teleport>
</template>

<script setup>
import { useIntervalFn } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import BaseFileUpload from "~vue/components/BaseFileUpload.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import BaseToggleGroup from "~vue/components/BaseToggleGroup.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const NAME_COLUMN = "name";
const EXPECTED_COLUMNS = [NAME_COLUMN, "job_title", "email", "in_team", "in_org", "comfort_level", "notes"];
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const props = defineProps({
    csvTemplateUrl: { type: String, required: true },
    csvPreviewUrl: { type: String, required: true },
    csvImportUrl: { type: String, required: true },
    footerTarget: { type: String, required: true },
});

const emit = defineEmits(["completed", "imported", "close", "importing", "busy"]);
const { t } = useI18n();

const step = ref("upload");
const selectedFile = ref(null);
const autoOverwrite = ref(false);
const uploadError = ref("");
const previewData = ref(null);
const rowActions = ref({});
const duplicateActionOptions = computed(() => [
    { key: "skip", label: t("connections.csvImport.preview.skip") },
    { key: "overwrite", label: t("connections.csvImport.preview.overwrite") },
]);
const importResult = ref(null);
const isUploading = ref(false);
const isImporting = ref(false);
const parsedFileInfo = ref(null);
const overwriteFlash = ref(false);
const columnRefOpen = ref(false);
const errorDetailsOpen = ref(false);
const importProgress = ref(null);
const pollStatusUrl = ref(null);
const pollStartTime = ref(null);
const expectedColumns = EXPECTED_COLUMNS;

const hasImportableRows = computed(() => {
    if (!previewData.value) {
        return false;
    }
    return previewData.value.rows.some((row) => !row.errors.length);
});

const truncatedFileName = computed(() => {
    if (!selectedFile.value) {
        return "";
    }
    const name = selectedFile.value.name;
    const MAX_LENGTH = 40;
    if (name.length <= MAX_LENGTH) {
        return name;
    }
    return `${name.slice(0, MAX_LENGTH / 2)}...${name.slice(name.length - MAX_LENGTH / 2)}`;
});

const formattedFileSize = computed(() => {
    if (!selectedFile.value) {
        return "";
    }
    const size = selectedFile.value.size;
    if (size < 1024) {
        return t("connections.csvImport.fileSize.bytes", { size });
    } else if (size < 1024 * 1024) {
        return t("connections.csvImport.fileSize.kilobytes", { size: Math.floor(size / 1024) });
    }
    return t("connections.csvImport.fileSize.megabytes", { size: Math.floor(size / (1024 * 1024)) });
});

const missingExpectedColumns = computed(() => {
    if (!parsedFileInfo.value) {
        return [];
    }
    const detected = parsedFileInfo.value.detectedColumns;
    return EXPECTED_COLUMNS.filter((col) => !detected.includes(col));
});

const resultSummaryText = computed(() => {
    if (!importResult.value) {
        return "";
    }
    const { created, updated } = importResult.value;
    if (created && updated) {
        return t("connections.csvImport.result.createdAndUpdated", { created, updated });
    }
    if (created) {
        return t("connections.csvImport.result.createdOnly", { count: created }, created);
    }
    if (updated) {
        return t("connections.csvImport.result.updatedOnly", { count: updated }, updated);
    }
    return t("connections.csvImport.result.noChanges");
});

const progressText = computed(() =>
    importProgress.value ? t("connections.csvImport.importing.progress", { processed: importProgress.value.processed, total: importProgress.value.total }) : "",
);

const columnReference = computed(() => [
    { column: NAME_COLUMN, required: true, format: t("connections.csvImport.columns.formatText"), example: t("connections.csvImport.columns.exampleName") },
    { column: "job_title", required: false, format: t("connections.csvImport.columns.formatText"), example: t("connections.csvImport.columns.exampleJobTitle") },
    { column: "email", required: false, format: t("connections.csvImport.columns.formatEmail"), example: t("connections.csvImport.columns.exampleEmail") },
    // The importer only accepts these literal yes/no values, so they stay untranslated.
    { column: "in_team", required: false, format: "yes / no", example: "yes" },
    { column: "in_org", required: false, format: "yes / no", example: "yes" },
    { column: "comfort_level", required: false, format: t("connections.csvImport.columns.formatComfortLevel"), example: "3" },
    { column: "notes", required: false, format: t("connections.csvImport.columns.formatText"), example: t("connections.csvImport.columns.exampleNotes") },
]);

const progressPercent = computed(() => {
    if (!importProgress.value || !importProgress.value.total) {
        return 0;
    }
    return Math.round((importProgress.value.processed / importProgress.value.total) * 100);
});

const { pause: pausePoll, resume: resumePoll } = useIntervalFn(
    async () => {
        if (!pollStatusUrl.value) {
            return;
        }

        if (Date.now() - pollStartTime.value > POLL_TIMEOUT_MS) {
            pausePoll();
            uploadError.value = t("connections.csvImport.errors.timedOut");
            step.value = "preview";
            logUserInteraction(AmplitudeEventNames.CSV_IMPORT_FAILED, {
                error_message: "timeout",
            });
            return;
        }

        const { data } = await useFetch(pollStatusUrl.value).get().json();
        if (!data.value) {
            return;
        }

        importProgress.value = data.value;

        if (data.value.status === "completed") {
            pausePoll();
            importResult.value = {
                created: data.value.created,
                updated: data.value.updated,
                skipped: data.value.skipped,
                errors: data.value.errors,
            };
            step.value = "result";
            emit("completed", importResult.value);
            logUserInteraction(AmplitudeEventNames.CSV_IMPORT_SUCCESS, {
                created: data.value.created,
                updated: data.value.updated,
                skipped: data.value.skipped,
                error_count: data.value.errors?.length || 0,
            });
        } else if (data.value.status === "failed") {
            pausePoll();
            uploadError.value = data.value.error_message || t("connections.csvImport.errors.importFailed");
            step.value = "preview";
            logUserInteraction(AmplitudeEventNames.CSV_IMPORT_FAILED, {
                error_message: data.value.error_message || "Import failed",
            });
        }
    },
    POLL_INTERVAL_MS,
    { immediate: false },
);

const downloadTemplate = async () => {
    const response = await fetchApi(props.csvTemplateUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my_people_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    logUserInteraction(AmplitudeEventNames.CSV_IMPORT_TEMPLATE_DOWNLOADED);
};

const parseFileHeaders = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text !== "string") {
            return;
        }
        const lines = text.split(/\r?\n/).filter((line) => line.trim());
        if (lines.length === 0) {
            return;
        }
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ""));
        parsedFileInfo.value = {
            rowCount: lines.length - 1,
            detectedColumns: headers,
        };
    };
    reader.readAsText(file);
};

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const validateFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE) {
        uploadError.value = t("connections.csvImport.errors.fileTooLarge");
        return false;
    }
    return true;
};

const handleFileSelect = (files) => {
    const file = files[0];
    if (!file) {
        return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
        uploadError.value = t("connections.csvImport.errors.dropCsv");
        return;
    }
    if (!validateFileSize(file)) {
        return;
    }
    selectedFile.value = file;
    uploadError.value = "";
    columnRefOpen.value = false;
    parseFileHeaders(file);
    logUserInteraction(AmplitudeEventNames.CSV_IMPORT_FILE_SELECTED, {
        file_size: file.size,
    });
};

const clearFile = () => {
    selectedFile.value = null;
    parsedFileInfo.value = null;
    uploadError.value = "";
};

const handleUpload = async () => {
    if (!selectedFile.value) {
        return;
    }
    isUploading.value = true;
    uploadError.value = "";

    const formData = new FormData();
    formData.append("file", selectedFile.value);

    const { data, error } = await useFetch(props.csvPreviewUrl).post(formData).json();

    isUploading.value = false;

    if (error.value) {
        const errorData = data.value;
        uploadError.value = errorData?.error || t("connections.csvImport.errors.processFailed");
        return;
    }

    previewData.value = data.value;

    // Initialize row actions for duplicates
    const actions = {};
    for (const row of previewData.value.rows) {
        if (row.is_duplicate) {
            actions[row.row_index] = "skip";
        }
    }
    rowActions.value = actions;

    step.value = "preview";
    logUserInteraction(AmplitudeEventNames.CSV_IMPORT_PREVIEW_SHOWN, {
        row_count: previewData.value.rows.length,
        new_count: previewData.value.summary.new,
        duplicate_count: previewData.value.summary.duplicates,
        error_count: previewData.value.summary.errors,
    });
};

const handleImport = async () => {
    if (!previewData.value) {
        return;
    }
    isImporting.value = true;

    const rows = previewData.value.rows
        .filter((row) => !row.errors.length)
        .map((row) => {
            const isDuplicate = row.is_duplicate;
            const shouldOverwrite = autoOverwrite.value || rowActions.value[row.row_index] === "overwrite";
            const shouldSkip = isDuplicate && !shouldOverwrite;

            return {
                row_index: row.row_index,
                name: row.name,
                email: row.email,
                job_title: row.job_title,
                in_team: row.in_team,
                in_org: row.in_org,
                comfort_level: row.comfort_level,
                notes: row.notes,
                existing_connection_id: row.existing_connection_id,
                overwrite: isDuplicate && shouldOverwrite,
                skip: shouldSkip,
            };
        });

    const payload = ref({ rows });
    const { data, error } = await useFetch(props.csvImportUrl).post(payload).json();

    isImporting.value = false;

    if (error.value) {
        uploadError.value = t("connections.csvImport.errors.importFailed");
        return;
    }

    pollStatusUrl.value = data.value.status_url;
    importProgress.value = { processed: 0, total: rows.length };
    pollStartTime.value = Date.now();
    step.value = "importing";
    logUserInteraction(AmplitudeEventNames.CSV_IMPORT_SUBMITTED, {
        total_rows: rows.length,
        overwrite_count: rows.filter((r) => r.overwrite).length,
        skip_count: rows.filter((r) => r.skip).length,
    });
    resumePoll();
};

const resetToUpload = () => {
    step.value = "upload";
    previewData.value = null;
    rowActions.value = {};
    autoOverwrite.value = false;
};

const handleDone = () => {
    emit("imported", importResult.value);
};

// Flash duplicate rows when auto-overwrite is toggled on
watch(autoOverwrite, (newVal) => {
    if (newVal && previewData.value?.summary.duplicates > 0) {
        overwriteFlash.value = true;
        setTimeout(() => {
            overwriteFlash.value = false;
        }, 800);
    }
});

watch(step, (value) => emit("importing", value === "importing"));
watch(
    () => isImporting.value || step.value === "importing" || step.value === "result",
    (value) => emit("busy", value),
);

onBeforeUnmount(() => {
    if (step.value !== "result") {
        logUserInteraction(AmplitudeEventNames.CSV_IMPORT_CANCELLED, {
            step: step.value,
        });
    }
});
</script>

<style scoped>
@layer components {
    .overwrite-flash {
        background-color: var(--color-chip-status-pending-bg);
        animation: overwrite-flash 0.8s ease-out;
    }

    @media (prefers-reduced-motion: reduce) {
        .overwrite-flash {
            animation: none;
        }
    }
}

@keyframes overwrite-flash {
    0% {
        background-color: var(--color-notification-warning-border);
    }
    100% {
        background-color: var(--color-chip-status-pending-bg);
    }
}
</style>
